import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, DollarSign, TrendingUp, Info } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const InvestmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [investment, setInvestment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvestment();
  }, [id]);

  const fetchInvestment = async () => {
    try {
      const response = await api.get(`/investments/${id}`);
      setInvestment(response.data);
    } catch (error) {
      toast.error('Failed to load investment details');
      navigate('/portfolio');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-48 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-64"></div>
        </div>
      </div>
    );
  }

  if (!investment) return null;

  const getStatusColor = (status) => {
    const colors = {
      Current: 'text-green-600 bg-green-50',
      Late: 'text-red-600 bg-red-50',
      Settled: 'text-blue-600 bg-blue-50',
      'Paid in Full': 'text-violet-600 bg-violet-50',
    };
    return colors[status] || 'text-slate-600 bg-slate-50';
  };

  return (
    <div className="space-y-6" data-testid="investment-details-page">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{investment.investment_id}</h1>
          <p className="text-slate-600 mt-1">Investment Details</p>
        </div>
        <span className={`px-4 py-2 rounded-lg font-semibold ${getStatusColor(investment.status)}`}>
          {investment.status}
        </span>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="text-sm text-slate-600 mb-1">Purchase Price</div>
          <div className="text-2xl font-bold text-slate-900">
            ${investment.purchase_price.toLocaleString()}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="text-sm text-slate-600 mb-1">Paid Back</div>
          <div className="text-2xl font-bold text-green-600">
            ${investment.paid_back.toLocaleString()}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-violet-50 rounded-lg">
              <Info className="w-6 h-6 text-violet-600" />
            </div>
          </div>
          <div className="text-sm text-slate-600 mb-1">Term Completed</div>
          <div className="text-2xl font-bold text-slate-900">{investment.term_percent.toFixed(1)}%</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-amber-50 rounded-lg">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
          </div>
          <div className="text-sm text-slate-600 mb-1">RTR Paid</div>
          <div className="text-2xl font-bold text-slate-900">{investment.rtr_paid.toFixed(1)}%</div>
        </div>
      </div>

      {/* Detailed Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Financial Details */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Financial Details</h2>
          <div className="space-y-4">
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Net RTR</span>
              <span className="font-semibold text-slate-900">
                ${investment.net_rtr.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Principal</span>
              <span className="font-semibold text-slate-900">
                ${investment.principal.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Avg Monthly Remittance</span>
              <span className="font-semibold text-slate-900">
                ${investment.avg_monthly_remit.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Commission</span>
              <span className="font-semibold text-slate-900">{investment.commission}%</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Factor Rate</span>
              <span className="font-semibold text-slate-900">{investment.factor_rate}</span>
            </div>
          </div>
        </div>

        {/* Loan Details */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Loan Details</h2>
          <div className="space-y-4">
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">FICO Score</span>
              <span className="font-semibold text-slate-900">{investment.fico}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Term Months</span>
              <span className="font-semibold text-slate-900">{investment.term_months}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Advance Type</span>
              <span className="font-semibold text-slate-900">{investment.advance_type}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Lien Position</span>
              <span className="font-semibold text-slate-900">{investment.lien_position}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Days Outstanding</span>
              <span className="font-semibold text-slate-900">{investment.days_outstanding}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-900 mb-6">Additional Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-sm text-slate-600 mb-1">Criteria Set</div>
            <div className="font-medium text-slate-900">{investment.criteria_set}</div>
          </div>
          <div>
            <div className="text-sm text-slate-600 mb-1">Date Funded</div>
            <div className="font-medium text-slate-900">{investment.date_funded || 'N/A'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentDetails;